import { HttpClient, HttpParams, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, lastValueFrom, of, retry, switchMap, throwError, timer } from 'rxjs';
import { CrmtFile, ICrmtFile } from '../utils/crmt-file';
import { environment } from './../../environments/environment';
import { CrmtFileFactory } from '../utils/crmt-file-parser';
import { FileListItem } from '../components/cloud-file-selector/cloud-file-selector.component';
import * as JSZip from 'jszip';
import * as FileSaver from 'file-saver';
import { CognitoAuthUtils } from '../utils/auth-utils';

export type CommandJobRequest = {

  command: string;
  swVersion: number;
  stockType: boolean;
  stockNumber: string;
  languageCode: number;
  devices: string[] | null;
  groups: string[] | null;
  scheduleLater: boolean;
  startTime?: string;
}

export type DeployJobRequest = {

  fileType: string;
  filename?: string;
  iniFilename?: string;
  zipFilename?: string;
  groups?: string[] | null;
  devices?: string[] | null;
  scheduleLater: boolean;
  startTime?: string;
}

type CancelJobBody = {
  jobId: string,
  comment?: string,
}

type DeleteFileBody = {
  fileName: string,
  comment?: string,
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  // The url for the REST API
  private apiUrl = environment.backendConfig.url

  private thingArn = ''
  private accountInfo: any

  constructor(private http: HttpClient) { }

  // Implements CRMT-LTE-SSRS-5568
  public fleetListDevices(nextToken: string | undefined, 
    paginationPageSize: number): Observable<any[]> {

    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {
        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken)
        }
        const url = this.apiUrl + 'fleet/list-devices'
        const body = {
          nextToken,
          paginationPageSize
        }
        return this.http.post<any>(url, body, options).pipe(
          switchMap((response: any) => {
            return of(response.message.things)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );
  }

  public registerDevice(
    thingType: string,
    serialNumber: string) {

    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {
        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken)
        }
        const url = this.apiUrl + 'provision'
        const body = {
          thingType,
          serialNumber
        }
        return this.http.post<any>(url, body, options).pipe(
          switchMap((response: any) => {
            return of(response)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );
  }

  public getAuditLog(
    startTime: number,
    endTime: number
  ): Observable<any[]> {

    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {

        let queryParams = new HttpParams();
        queryParams = queryParams.append("startTime", startTime);
        queryParams = queryParams.append("endTime", endTime);

        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken),
          params: queryParams
        }
        const url = this.apiUrl + 'audit'
        return this.http.get<any[]>(url,options).pipe(
          switchMap((response: any) => {
            return of(response.message)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );
  }

  public getJobList(nextToken: string, 
    paginationPageSize: number): Observable<any[]> {

    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {
        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken)
        }
        const url = this.apiUrl + 'job-list'
        const body = {
          nextToken,
          paginationPageSize
        }
        return this.http.post<any>(url, body, options).pipe(
          switchMap((response: any) => {
            return of(response)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );
  }

  public getFileList(
    fileType: string,
    hardwareType: string): Observable<any[]> {

    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {

        let queryParams = new HttpParams();
        queryParams = queryParams.append("fileType", fileType);
        queryParams = queryParams.append("hardwareType", hardwareType);

        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken),
          params: queryParams
        }
        const url = this.apiUrl + 'file-list'
        return this.http.get<any>(url,options).pipe(
          switchMap((response: any) => {
            return of(response.message)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );    
  }

  public getSoftwarePackageFileList(folderName: string): Observable<any[]> {

    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {

        let queryParams = new HttpParams();
        queryParams = queryParams.append("folderPath", folderName);

        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken),
          params: queryParams
        }
        const url = this.apiUrl + 'software-package-file-list'
        return this.http.get<any>(url,options).pipe(
          switchMap((response: any) => {
            return of(response.message)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    ); 
  }

  public listDeviceTypes(): Observable<string[]> {

    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {
        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken)
        }
        const url = this.apiUrl + 'device-types'
        return this.http.get<any>(url,options).pipe(
          switchMap((response: any) => {
            return of(response.message)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );
  }

  public getAccountInfo(): Observable<any> {

    if (this.accountInfo) {

      // Use cached data
      return of(this.accountInfo)
    }
    else {
      return CognitoAuthUtils.getAccessToken().pipe(  
        switchMap((accessToken: string) => {
          const options = {
            headers: new HttpHeaders().set('Authorization', accessToken)
          }
          const url = this.apiUrl + 'account-info'
          return this.http.get<any>(url,options).pipe(
            switchMap((response: any) => {
              this.accountInfo = response.message
              return of(this.accountInfo)
            }),
            catchError((error) => {
              console.error(`Error making API ${url} call`, error);
              throw error;  
            })
          );
        })
      );
      
    }
  }

  public groupDeviceTree(): Observable<string[]> {

    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {
        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken)
        }
        const url = this.apiUrl + 'fleet/group-device-tree'
        return this.http.get<any>(url,options).pipe(
          switchMap((response: any) => {
            return of(response)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );
  }

  public fleetDeviceTreeInGroup(nextToken: string | undefined, 
    paginationPageSize: number, groupName: string, 
    filePath: string[]): Observable<string[]> {

    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {
        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken)
        }
        const url = this.apiUrl + 'fleet/list-devices-in-group'
        const body = {
          paginationPageSize,
          nextToken,
          groupName,
          filePath
        }
        return this.http.post<any>(url, body, options).pipe(
          switchMap((response: any) => {
            return of(response)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );
  }

  public fleetListGroups(): Observable<string[]> {

    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {
        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken)
        }
        const url = this.apiUrl + 'fleet/list-groups'
        return this.http.get<any>(url,options).pipe(
          switchMap((response: any) => {
            return of(response.message)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );
  }

  public fleetAddGroup(thingGroupName: string, 
    parentGroupName: string, thingGroupDescription: string) {

    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {
        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken)
        }
        const url = this.apiUrl + 'fleet/add-thing-group'
        const body = {
          thingGroupName,
          parentGroupName,
          thingGroupDescription
        }
        return this.http.post<any>(url, body, options).pipe(
          switchMap((response: any) => {
            return of(response)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );
  }

  public fleetRemoveGroup(thingGroupName: string) {

    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {
        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken)
        }
        const url = this.apiUrl + 'fleet/remove-thing-group'
        const body = { thingGroupName }
        return this.http.post<any>(url, body, options).pipe(
          switchMap((response: any) => {
            return of(response)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );
  }

  public fleetMoveThingToGroup(
    oldGroupName: string,
    newGroupName: string,
    thingName: string) {

    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {
        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken)
        }
        const url = this.apiUrl + 'fleet/move-thing-to-group'
        const body = {
          oldGroupName,
          newGroupName,
          thingName
        }
        return this.http.post<any>(url, body, options).pipe(
          switchMap((response: any) => {
            return of(response)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );
  }

  public fleetAddThingToGroup(
    newGroupName: string,
    thingName: string) {

    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {
        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken)
        }
        const url = this.apiUrl + 'fleet/add-thing-to-group'
        const body = {
          newGroupName,
          thingName
        }
        return this.http.post<any>(url, body, options).pipe(
          switchMap((response: any) => {
            return of(response)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );
  }

  public fleetRemoveThingFromGroup(
    group: string,
    thingName: string) {

    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {
        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken)
        }
        const url = this.apiUrl + 'fleet/remove-thing-from-group'
        const body = {
          group,
          thingName
        }
        return this.http.post<any>(url, body, options).pipe(
          switchMap((response: any) => {
            return of(response)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );
  }

  public getJobStatus(jobId: string) {

    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {
        let queryParams = new HttpParams();
        queryParams = queryParams.append("jobId", jobId);

        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken),
          params: queryParams
        }
        const url = this.apiUrl + 'job/job-status'
        return this.http.get<any>(url,options).pipe(
          switchMap((response: any) => {
            return of(response)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );

  }

  public getJobStatusDetails(
    jobId: string,
    devices: string[]) {

    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {
        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken)
        }
        const url = this.apiUrl + 'job/job-status-details'
        const body = {
          jobId,
          devices
        }
        return this.http.post<any>(url, body, options).pipe(
          switchMap((response: any) => {
            return of(response)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );
  }

  public sendCommand(commandJob: CommandJobRequest) {

     return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {
        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken)
        }
        const url = this.apiUrl + 'command'

        return this.http.post<any>(url, commandJob, options).pipe(
          switchMap((response: any) => {
            return of(response)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );

  }

  public deploy(deployJob: DeployJobRequest) {

    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {
        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken)
        }
        const url = this.apiUrl + 'deploy'

        return this.http.post<any>(url, deployJob, options).pipe(
          switchMap((response: any) => {
            return of(response)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );
  }

  public cancelJobService(cancelJobBody: CancelJobBody[]): Observable<any> {

    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {
        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken)
        }
        const url = this.apiUrl + 'cancel-job'

        return this.http.put<any>(url, cancelJobBody, options).pipe(
          switchMap((response: any) => {
            return of(response)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );    

  }

  public retrieve(
    fileType: string,
    device: string): Observable<any> {

    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {
        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken)
        }
        const url = this.apiUrl + 'retrieve'
        const body = {
          fileType,
          tempFile: true,
          device
        }
        return this.http.post<any>(url, body, options).pipe(
          switchMap((response: any) => {
            return of(response)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );

  }

  public getFile(fileType: string, device: string): Observable<CrmtFile> {

    const retrieveObs = this.retrieve(fileType, device)

    return retrieveObs.pipe(
      switchMap(result => {

        if (!result.message.lambdaResponse.succeeded) {
          if(result.message.status === 'CANCELED'){
            throw new Error(result.message.status)
          }else{
            throw new Error("Retrieve operation failed")
          }
          
        }
        const filename = result.message.filename

        return this.download(filename, fileType)
      })
    )
  }

  // Fetch a file from AWS
  public download(filename: string, fileType: string): Observable<CrmtFile> {

    // Request a presigned url
    const urlObs = this.getDownloadUrl(filename)

    const options: any = {
      responseType: 'arraybuffer',
      headers: {
        Accept: 'binary/octet-stream'
      }
    }

    return urlObs.pipe(
      switchMap(presignedUrl => this.http.get(presignedUrl, options)),
      switchMap(data => of(CrmtFileFactory.parse(fileType, data))),
      catchError((err: HttpErrorResponse) => {
        console.error('An error occurred:', err.error);
        return throwError(() => err);
      })
    )
  }


  // Fetch a file from AWS
  public async downloadSoftwarePackage(folderPath: string, data: FileListItem[]) {
    var folderName = folderPath.substring(folderPath.lastIndexOf('/') + 1);
    var zip = new JSZip();

    // Download all files concurrently
    const downloadPromises = data.map(async (item) => {
      const fileToDownload = `${folderPath}/${item.Key}`;

      // Get download URL
      const url = await lastValueFrom(this.getDownloadUrl(fileToDownload));

      // Download file as blob
      const blob = await lastValueFrom(this.downloadSoftwarePackageFile(url));

      // Add the blob to the zip file
      const fileName = fileToDownload.substring(fileToDownload.lastIndexOf('/') + 1);
      zip.file(fileName, blob);
    });

    // Wait for all files to be downloaded and added to the zip
    await Promise.all(downloadPromises);

    // Generate zip and save
    zip.generateAsync({ type: 'blob' })
      .then((content) => {
        FileSaver.saveAs(content, `${folderName}.zip`)
      })
      .catch((err) => {
        console.error("An error occurred while generating zip:", err);
        return throwError(() => err);
      });
  }

  private downloadSoftwarePackageFile(signedUrl: string) {
    return this.http.get(signedUrl, { responseType: 'blob' });
  }

  public deleteFileFromAWS(deleteFileBody: DeleteFileBody[]): Observable<any> {
    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {
        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken),
          body: deleteFileBody
        }
        const url = this.apiUrl + 'delete-file'

        return this.http.delete<any>(url, options).pipe(
          switchMap((response: any) => {
            return of(response)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );    

  }

  // Get presigned url for uploading
  public getUploadUrl(filename: string): Observable<string> {

    const url = this.apiUrl + 'upload'
    return this.getPresignedUrl(url, filename)
  }

  // Get a presigned url for downloading
  public getDownloadUrl(filename: string): Observable<string> {

    const url = this.apiUrl + 'download'
    return this.getPresignedUrl(url, filename)
  }

  // Call the lambda the generates urls
  private getPresignedUrl(url: string, filename: string): Observable<string> {
    return CognitoAuthUtils.getAccessToken().pipe(  
      switchMap((accessToken: string) => {
        let queryParams = new HttpParams();
        queryParams = queryParams.append("filename", filename);

        const options = {
          headers: new HttpHeaders().set('Authorization', accessToken),
          params: queryParams
        }
        return this.http.get<any>(url,options).pipe(
          switchMap((response: any) => {
            return of(response.message.url)
          }),
          catchError((error) => {
            console.error(`Error making API ${url} call`, error);
            throw error;  
          })
        );
      })
    );
  }

  public putFile(filename: string, fileType: string, devices: string[], file: ICrmtFile): Observable<any> {

    // Encode the file file - this probably needs a virtual method
    const rawData = file.toRawData()

    // Upload the file
    const uploadObs = this.uploadFile(filename, rawData)

    // Deploy the file
    return uploadObs.pipe(
      switchMap(() => {

        const job: DeployJobRequest = {
          fileType,
          filename: `uploads/${filename}`,
          devices,
          scheduleLater: false
        }

        return this.deploy(job)
      })
    )
  }

  // Upload a file or data to AWS
  public uploadFile(
    filename: string,
    data: File | Blob | ArrayBuffer | string): Observable<any> {

    const urlObs = this.getUploadUrl(filename);

    return urlObs.pipe(
      switchMap(url => this.http.put<any>(url, data))
    )
  }

  public save(fileType: string, crmtFile: ICrmtFile) {

    let link = document.createElement('a');
    let fileName = this.getFileName(crmtFile, fileType);
    link.download =  fileName == "" ? fileType : fileName;
    const rawData = crmtFile.toRawData()

    let blob = new Blob([rawData], { type: 'binary/octet-stream' });

    link.href = URL.createObjectURL(blob);

    link.click();

    URL.revokeObjectURL(link.href);
  }

  public setThingArn(arn: string) {
    this.thingArn = arn
  }

  public getThingArn() {
    return this.thingArn
  }

  private getFileName(crmtFile: ICrmtFile, fileType:string){
    let fileName = "";
    const extension = this.getFileExtension(fileType);
    if(crmtFile.header != undefined){
      let header  = crmtFile.header;
      fileName = `${header.name} V${header.majorVersion}.${header.minorVersion}`;
      if(!fileName.endsWith(extension)){
        return fileName.concat(`.${extension}`);
      }
    }  
    return fileName;
  }
  getFileExtension(fileType: string): string {
        if(fileType){
          const extensions: { [key: string]: string } = {
            'standard-phonebook': 'spb',  
            'extended-phonebook': 'epb',     
            'sms-database': 'sms',
            'uus-database' : 'uus'
          };
          return extensions[fileType] || 'db';
        }
        return '';
    
  }

}
